import { useEffect, useRef, useState } from "react";
import PropertyFacts from "../components/PropertyFacts";
import Spinner from "../components/Spinner";
import RegenerateBar from "../components/RegenerateBar";
import { callAI, track } from "../lib/api";
import { propFactsLine } from "../lib/property";
import { getStageState } from "../lib/journey";

// Stage 6 — Listing creation.
// Auto-runs on first mount. Uses the valuation output if present so the
// list price baked into the copy is consistent with stage 1.

const SYSTEM = `You are an elite real estate listing copywriter with 20 years of experience writing listings that consistently sell above asking price. Your copy is vivid, specific, and grounded entirely in real property facts — you never invent features. You write for buyers who are scrolling fast: the headline must stop them, the first sentence must hook them, every sentence must earn its place. No emojis. No filler. No "don't miss this opportunity". No "imagine yourself".`;

const buildPrompt = (address, factsLine, priceLine, prepNotes, stagingNotes, note) => {
  const extras = [prepNotes, stagingNotes].filter(Boolean).join("\n");
  return `Write a compelling MLS listing for this property.

Property: ${address}
Facts: ${factsLine}
${priceLine ? `Pricing context: ${priceLine}` : ""}
${extras ? `Recent work & condition notes:\n${extras}` : ""}
${note ? `Seller's guidance for this regeneration — factor this in: ${note}` : ""}

Use web search to find 2–3 specific neighborhood highlights (walkability, schools, transit, nearby amenities) for this address. Weave them naturally into the copy — don't list them mechanically.

Output EXACTLY this format:

HEADLINE: (one punchy line under 72 chars — lead with the strongest feature or location hook)

DESCRIPTION:
(Three tight paragraphs. First: open with the property's strongest physical hook — a specific room, view, or feature. Second: neighborhood and lifestyle. Third: practical strengths — layout, condition, updates — and a direct closing line that drives action. Each paragraph 2–4 sentences. Total under 200 words.)`;
};

const parse = (text) => {
  const hMatch = text.match(/HEADLINE:\s*(.+?)(?:\n|$)/i);
  const dMatch = text.match(/DESCRIPTION:\s*([\s\S]+)/i);
  return {
    headline: (hMatch?.[1] || "").trim(),
    body: (dMatch?.[1] || "").trim(),
  };
};

export default function ListingStage({ state, journey, update }) {
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [headline, setHeadline] = useState(state.data?.headline || "");
  const [body, setBody] = useState(state.data?.body || "");
  const [err, setErr] = useState(null);
  const fired = useRef(false);

  const run = async (note = "") => {
    setLoading(true);
    setErr(null);
    update({ status: "in_progress" });
    track("seller_listing_run", { address: journey.address, has_note: Boolean(note) });
    try {
      const valData  = getStageState(journey, "valuation").data;
      const prepData = getStageState(journey, "prep").data;
      const stagData = getStageState(journey, "staging").data;
      const priceLine = valData?.range
        ? `Target list-price range: $${Math.round(valData.range.low).toLocaleString()}–$${Math.round(valData.range.high).toLocaleString()}.`
        : "";
      const prepNotes  = prepData?.summary  || prepData?.text  || "";
      const stagNotes  = stagData?.summary  || stagData?.text  || "";
      const { text } = await callAI({
        system: SYSTEM,
        prompt: buildPrompt(journey.address, propFactsLine(journey.property), priceLine, prepNotes, stagNotes, note),
      });
      const parsed = parse(text);
      setHeadline(parsed.headline);
      setBody(parsed.body);
      update({
        status: "done",
        data: { ...parsed, generated_at: new Date().toISOString() },
      });
      track("seller_listing_done");
    } catch {
      update({ status: "pending" });
      setErr("Couldn't draft your listing copy. Try again.");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    if (state.status !== "done" && !state.data) run();
  }, []);

  const saveEdit = () => {
    update({ status: "done", data: { headline, body, generated_at: new Date().toISOString() } });
    setEditing(false);
    track("seller_listing_edited");
  };

  const data = state.data;
  return (
    <>
      <PropertyFacts property={journey.property} />
      {loading && <Spinner label="Drafting your listing copy…" />}
      {!loading && !data && err && (
        <div>
          <div className="s-intake-err" style={{ marginBottom: 12 }}>{err}</div>
          <button type="button" className="s-btn amber" onClick={() => run()}>Try again</button>
        </div>
      )}
      {!loading && data && !editing && (
        <>
          <h2 className="s-list-headline">{data.headline || "—"}</h2>
          <div className="s-list-body">{data.body}</div>
          <div style={{ marginTop: 12 }}>
            <button type="button" className="s-link-btn" onClick={() => { setHeadline(data.headline || ""); setBody(data.body || ""); setEditing(true); }}>
              edit
            </button>
          </div>
          <RegenerateBar
            onRegenerate={(note) => run(note)}
            loading={loading}
            placeholder="e.g. shorter, emphasize the backyard, drop the price mention…"
          />
        </>
      )}
      {!loading && editing && (
        <>
          <input
            value={headline}
            onChange={(e) => setHeadline(e.target.value)}
            className="s-list-edit headline"
            placeholder="Headline"
          />
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className="s-list-edit"
            placeholder="Description"
          />
          <div style={{ marginTop: 10 }}>
            <button type="button" className="s-link-btn" onClick={saveEdit}>save</button>
            <button type="button" className="s-link-btn" onClick={() => setEditing(false)}>cancel</button>
          </div>
        </>
      )}
    </>
  );
}
